const phases = [
  {
    number: '01',
    phase: 'Phase 1',
    title: 'Foundation & Awareness',
    years: 'Freshman / Sophomore Year',
    icon: 'ri-seedling-fill',
    color: 'from-emerald-500/20 to-emerald-900/5',
    border: 'border-emerald-500/30',
    accentColor: 'text-emerald-400',
    badgeColor: 'bg-emerald-500/10 border-emerald-500/20',
    items: [
      "Build your athlete's recruiting profile",
      'Create a standout highlight video',
      'Identify target programs by division',
      'Understand NCAA eligibility rules',
      'Start a coach contact database',
      'Draft your first outreach email',
    ],
  },
  {
    number: '02',
    phase: 'Phase 2',
    title: 'Build Exposure & Relationships',
    years: 'Sophomore / Junior Year',
    icon: 'ri-rocket-2-fill',
    color: 'from-[#f5a623]/20 to-[#d4891a]/5',
    border: 'border-[#f5a623]/30',
    accentColor: 'text-[#f5a623]',
    badgeColor: 'bg-[#f5a623]/10 border-[#f5a623]/20',
    items: [
      'Send targeted coach outreach campaigns',
      'Attend showcase & camp evaluations',
      'Follow up with interested programs',
      'Update highlights after each season',
      'Request unofficial visits strategically',
      'Track all coach communication',
    ],
  },
  {
    number: '03',
    phase: 'Phase 3',
    title: 'Conversion & Commitment',
    years: 'Junior / Senior Year',
    icon: 'ri-flag-2-fill',
    color: 'from-sky-500/20 to-sky-900/5',
    border: 'border-sky-400/30',
    accentColor: 'text-sky-400',
    badgeColor: 'bg-sky-500/10 border-sky-500/20',
    items: [
      'Evaluate & compare scholarship offers',
      'Navigate official campus visits',
      'Negotiate financial aid packages',
      'Understand NLI & signing requirements',
      'Prepare for NCAA academic clearance',
      'Own your Signing Day moment',
    ],
  },
];

export default function WhatsInsideSection() {
  return (
    <section id="whats-inside" className="bg-[#060e1a] py-20 md:py-28 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#f5a623]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#f5a623]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-map-2-fill text-[#f5a623] text-sm" />
            <span className="text-[#f5a623] font-body text-xs font-semibold uppercase tracking-widest">The Full Playbook</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight">
            Your 4-Year
            <span className="block text-[#f5a623]">Recruiting Roadmap.</span>
          </h2>
          <p className="font-body text-lg text-white/60 max-w-2xl mx-auto mt-4">
            Every step, every deadline, every template — organized into three strategic phases so nothing falls through the cracks.
          </p>
        </div>

        {/* Phase cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {phases.map((phase, i) => (
            <div
              key={i}
              className={`relative bg-gradient-to-b ${phase.color} border ${phase.border} rounded-2xl p-6 md:p-8 flex flex-col gap-6`}
            >
              {/* Phase number background */}
              <div className="absolute top-4 right-4 font-display text-7xl font-bold text-white/4 select-none leading-none">
                {phase.number}
              </div>

              {/* Icon + phase label */}
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 flex items-center justify-center bg-[#0a1628] rounded-xl border border-white/10`}>
                  <i className={`${phase.icon} ${phase.accentColor} text-2xl`} />
                </div>
                <div className={`inline-flex items-center border ${phase.badgeColor} rounded-full px-3 py-1`}>
                  <span className={`font-body text-xs font-semibold uppercase tracking-wider ${phase.accentColor}`}>
                    {phase.phase}
                  </span>
                </div>
              </div>

              {/* Title */}
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white uppercase leading-tight">
                  {phase.title}
                </h3>
                <p className={`font-body text-sm font-semibold mt-1 ${phase.accentColor}`}>
                  {phase.years}
                </p>
              </div>

              {/* Items */}
              <ul className="flex flex-col gap-3 flex-1">
                {phase.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className={`ri-arrow-right-s-fill ${phase.accentColor} text-lg`} />
                    </div>
                    <span className="font-body text-sm text-white/75">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Progress bar visualization */}
        <div className="mt-12 bg-[#0a1628] border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 w-full">
              <div className="flex items-center justify-between mb-3">
                <span className="font-body text-xs text-white/50 uppercase tracking-wider">Freshman Year</span>
                <span className="font-body text-xs text-white/50 uppercase tracking-wider">Signing Day</span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 via-[#f5a623] to-sky-400 rounded-full" style={{ width: '100%' }} />
              </div>
              <div className="flex justify-between mt-2">
                <span className="font-body text-xs text-emerald-400 font-semibold">Foundation</span>
                <span className="font-body text-xs text-[#f5a623] font-semibold">Exposure</span>
                <span className="font-body text-xs text-sky-400 font-semibold">Commitment</span>
              </div>
            </div>
            <div className="md:w-64 text-center md:text-right">
              <p className="font-display text-xl font-bold text-white uppercase">Every step is covered.</p>
              <p className="font-body text-sm text-white/50 mt-1">No guessing. No missed windows.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
